/**
 * Widget local filter configurations
 * Fetches filter metadata from widget catalog API and transforms to FilterDefinition format
 */

import type { FilterDefinition } from "@/features/filters";
import { useLookupStore } from "@/store";
import { FEATURE_NAMES, logError } from "@/loggers";
import { useDashboardStore } from "@/views/ControlTowerView/store/dashboard";
import type { BucketRange } from "@/features/control-tower/interfaces/update-widget";
import i18n from "@/i18n";
/**
 * Default Config Item Interface
 * Represents threshold or bucket configuration from default_config array
 */
interface DefaultConfigItem {
  type: defaultConfigTypes;
  values?: string[];
  ranges?: BucketRange[];
}

export enum defaultConfigTypes {
  THRESHOLD = "THRESHOLD",
  BUCKETS = "BUCKETS"
}

export const BUCKET_PREFIX = "bucket";
export const THRESHOLD_PREFIX = "threshold";

export const MAX_THRESHOLD_DAYS = 180;
export const MIN_THRESHOLD_DAYS = 0;

enum lookSources {
  DYNAMIC = "DYNAMIC",
  STATIC = "STATIC"
}
enum controlTypes {
  TYPEAHEAD = "TYPEAHEAD",
  TEXT = "TEXT",
  DATE = "DATE",
  RADIO = "RADIO",
  NUMBER = "NUMBER",
  SELECT = "SELECT"
}
enum SelectionType {
  SINGLE_SELECT = "SINGLE_SELECT",
  MULTI_SELECT = "MULTI_SELECT"
}
// Defer accessing the dashboard store until runtime to avoid
// initialization order issues during tests or module mocking.

/**
 * Widget Catalog Filter Schema Interface
 * Matches the backend filter configuration structure
 */
interface WidgetFilterConfig {
  key: string;
  label: string;
  controlType: controlTypes;
  dataType: "STRING" | "NUMBER" | "BOOLEAN" | "DATE";
  source: lookSources;
  limit?: number;
  selectionType?: SelectionType;
  unit?: string;
  dynamicSource?: {
    source: string;
    endpoint: string;
    fields?: string;
  };
  staticSource?: Array<{
    label: string;
    value: string;
  }>;
  placeholder?: string;
  searchable?: boolean;
  range?: string;
  helperText?: string;
  min?: number;
  max?: number;
  default?: string;
}

/**
 * Maps widget catalog controlType to our FilterDefinition type
 */
const mapControlTypeToFilterType = (
  controlType: controlTypes,
  selectionType?: SelectionType
): FilterDefinition["type"] => {
  const typeMap: Record<string, FilterDefinition["type"]> = {
    TYPEAHEAD: selectionType === SelectionType.MULTI_SELECT ? "typeahead-multiselect" : "typeahead",
    SELECT: selectionType === SelectionType.MULTI_SELECT ? "select-multiselect" : "select",
    TEXT: "text",
    DATE: "input-date",
    RADIO: "radio",
    NUMBER: "number-stepper"
  };

  return typeMap[controlType] || "text";
};

/**
 * Per-widget override for the THRESHOLD stepper's label in the Configure widget
 * panel, keyed by `widget_catalog_type`. Thresholds are otherwise rendered
 * label-less under the generic "Thresholds" section heading, which is fine when
 * the number is self-explanatory ("Delayed by N days") but not when it is a
 * buffer on top of another value. Plan Deviation compares against the initial
 * planned dwell time, so the number of days needs that stated above the input.
 */
const THRESHOLD_LABEL_OVERRIDES: Record<string, string> = {
  TRANSHIPMENT_DWELL_PLAN_DEVIATION: "CONTROL_TOWER.WIDGET_FILTERS.TRANSHIPMENT_DWELL_PLAN_DEVIATION_THRESHOLD_LABEL"
};

/**
 * Creates a single filter definition for a THRESHOLD config value
 */
const createThresholdFilterDefinition = (configIndex: number, valueIndex: number, value: string): FilterDefinition => {
  return {
    key: `${THRESHOLD_PREFIX}${configIndex}_${valueIndex}`,
    label: "CONTROL_TOWER.WIDGET_FILTERS.THRESHOLD",
    type: "number-stepper",
    hiddenlabel: true,
    suffix: i18n.global.t("CONTROL_TOWER.DAYS"),
    value: value ?? 0,
    min: String(MIN_THRESHOLD_DAYS),
    max: String(MAX_THRESHOLD_DAYS),
    storeZoneSuffix: "_config",
    placeholder: "Enter value"
  };
};

/**
 * Default bucket ranges used when no ranges are provided
 */
const DEFAULT_BUCKET_RANGES: BucketRange[] = [
  { min: 0, max: 7, enabled: true },
  { min: 8, max: 14, enabled: true },
  { min: 15, max: MAX_THRESHOLD_DAYS, enabled: true }
];

/**
 * Creates a single filter definition for BUCKETS config using the new ranges format.
 * Produces a single "bucket-range" filter whose value is the entire ranges array.
 */
const createBucketRangeFilterDefinition = (configIndex: number, ranges: BucketRange[]): FilterDefinition => {
  return {
    key: `${BUCKET_PREFIX}_ranges_${configIndex}`,
    label: "CONTROL_TOWER.WIDGET_FILTERS.BUCKET",
    type: "bucket-range" as FilterDefinition["type"],
    storeZoneSuffix: "_config",
    value: ranges
  };
};

/**
 * Transforms default_config items (THRESHOLD/BUCKETS) to FilterDefinition format
 *
 * THRESHOLD: emits N number-stepper FilterDefinitions (one per value)
 * BUCKETS:   emits 1 bucket-range FilterDefinition with the full ranges array
 *
 * @param configItem - The default config item from catalog
 * @param index - Index for unique key generation
 * @returns FilterDefinition[] for the threshold/bucket
 */
export const transformDefaultConfigToDefinition = (
  configItem: DefaultConfigItem,
  index: number
): FilterDefinition[] => {
  // --- BUCKETS: new ranges format ---
  if (configItem.type === defaultConfigTypes.BUCKETS) {
    const ranges =
      Array.isArray(configItem.ranges) && configItem.ranges.length > 0 ? configItem.ranges : DEFAULT_BUCKET_RANGES;
    return [createBucketRangeFilterDefinition(index, ranges)];
  }

  // --- THRESHOLD: unchanged ---
  const defs: FilterDefinition[] = [];
  if (Array.isArray(configItem.values) && configItem.values.length > 0) {
    for (const [i, val] of configItem.values.entries()) {
      defs.push(createThresholdFilterDefinition(index, i, val));
    }
  } else {
    defs.push({
      key: `config${index}`,
      label: "Configuration",
      type: "number-stepper",
      storeZoneSuffix: "_config",
      value: 0
    });
  }
  return defs;
};

/**
 * Determines which lookup pattern to use based on widget product/source
 * @param product - The widget product type (from catalog)
 * @returns "lookupUrl" for the products we need to call api endpoint other than lookupUrl, "optionKey" for others
 */
const getLookupPattern = (product?: string): "lookupUrl" | "optionKey" => {
  if (!product) return "optionKey"; // Default to optionKey pattern

  return "optionKey";
};

/**
 * Adds default value to filter definition based on filter type
 */
const addDefaultValue = (filterDef: FilterDefinition, catalogFilter: WidgetFilterConfig, filterType: string): void => {
  if (!catalogFilter.default) return;

  if (["select", "typeahead"].includes(filterType)) {
    const defaultOption = catalogFilter.staticSource?.find((opt) => opt.value === catalogFilter.default);
    filterDef.value = {
      label: defaultOption?.label || catalogFilter.default,
      value: catalogFilter.default
    };
  } else {
    filterDef.value = catalogFilter.default;
  }
};

/**
 * Configures number-specific properties for filter definition
 */
const configureNumberFilter = (filterDef: FilterDefinition, catalogFilter: WidgetFilterConfig): void => {
  if (catalogFilter.min !== undefined) filterDef.min = catalogFilter.min;
  if (catalogFilter.max !== undefined) filterDef.max = catalogFilter.max;

  if (catalogFilter.unit) {
    filterDef.description = catalogFilter.helperText
      ? `${catalogFilter.helperText} (${catalogFilter.unit})`
      : `Unit: ${catalogFilter.unit}`;
  }

  if (catalogFilter.key === "latestEtaUpdateDate" || catalogFilter.label === "ETA_UPDATED_WITHIN") {
    filterDef.suffix = i18n.global.t("CONTROL_TOWER.DAYS");
  }
};

/**
 * Configures lookup URL pattern for dynamic filters
 */
export const configureLookupUrlPattern = (
  filterDef: FilterDefinition,
  catalogFilter: WidgetFilterConfig,
  filterType: string
): void => {
  filterDef.lookupUrl = catalogFilter.dynamicSource?.endpoint;

  const rawFields = catalogFilter.dynamicSource?.fields;
  let labelField = "name";
  let valueField = "code";
  if (Array.isArray(rawFields)) {
    if (rawFields[0]) labelField = rawFields[0];
    if (rawFields[1]) valueField = rawFields[1];
  } else if (typeof rawFields === "string") {
    const parts = rawFields
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (parts[0]) labelField = parts[0];
    if (parts[1]) valueField = parts[1];
  }
  filterDef.config = {
    labelField,
    valueField,
    responseLimit: 50
  };

  filterDef.searchParam = "tSearch";

  if (catalogFilter.searchable && filterType.includes("typeahead")) {
    filterDef.debounce = 500;
    filterDef.maxOptions = 50;
    filterDef.minChars = 2;
  }
};

/**
 * Configures option key pattern for dynamic filters using prefetched lookups
 * Uses catalog-based zone prefix for widget-specific lookup storage
 */
const configureOptionKeyPattern = (
  filterDef: FilterDefinition,
  catalogFilter: WidgetFilterConfig,
  zonePrefix: string,
  catalogId?: string
): void => {
  const lookupStore = useLookupStore();
  let lookupKey = catalogFilter.dynamicSource?.fields;
  if (!lookupKey) {
    lookupKey = catalogFilter.key;
  }
  filterDef.optionKey = lookupKey;
  filterDef.searchParam = "query";

  // Construct widget-specific zone prefix using catalog ID
  const widgetZonePrefix = catalogId ? `control-tower-${catalogId}` : zonePrefix;

  // Fetch directly from 'items' key where prefetchLookup stores the data
  if (lookupStore.prefetchedLookups[widgetZonePrefix]?.items) {
    filterDef.options = lookupStore.prefetchedLookups[widgetZonePrefix].items;
  } else if (lookupStore.prefetchedLookups[widgetZonePrefix]?.[lookupKey]) {
    // Fallback: try fetching from the lookupKey directly (for object-based responses)
    filterDef.options = lookupStore.prefetchedLookups[widgetZonePrefix][lookupKey];
  } else {
    logError(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `[transformCatalogFilterToDefinition] No prefetched options found for ${catalogFilter.key} (lookupKey: ${lookupKey}, zone: ${widgetZonePrefix})`
    );
    filterDef.options = [];
  }
};

/**
 * Handles dynamic source configuration based on lookup pattern
 */
const configureDynamicSource = (
  filterDef: FilterDefinition,
  catalogFilter: WidgetFilterConfig,
  filterType: string,
  zonePrefix: string,
  product?: string,
  catalogId?: string
): void => {
  const lookupPattern = getLookupPattern(product);

  if (lookupPattern === "lookupUrl") {
    configureLookupUrlPattern(filterDef, catalogFilter, filterType);
  } else {
    configureOptionKeyPattern(filterDef, catalogFilter, zonePrefix, catalogId);
  }
};

/**
 * Transforms widget catalog filter config to FilterDefinition format
 * Handles both static and dynamic data sources
 * Uses lookupUrl pattern for emissions/risk-management, optionKey pattern for others
 */
const transformCatalogFilterToDefinition = async (
  catalogFilter: WidgetFilterConfig,
  zonePrefix: string,
  product?: string,
  catalogId?: string
): Promise<FilterDefinition> => {
  const filterType = mapControlTypeToFilterType(catalogFilter.controlType, catalogFilter.selectionType);

  // Prefix label/placeholder with i18n key path (API always sends simple keys like AMOUNT_OF_CARRIERS)
  const label = catalogFilter.label ? `CONTROL_TOWER.WIDGET_FILTERS.${catalogFilter.label}` : undefined;

  const placeholder = catalogFilter.placeholder
    ? `CONTROL_TOWER.WIDGET_FILTERS.${catalogFilter.placeholder}`
    : undefined;

  const filterDef: FilterDefinition = {
    key: catalogFilter.key,
    label,
    type: filterType,
    placeholder
  };

  addDefaultValue(filterDef, catalogFilter, filterType);

  if (catalogFilter.helperText) {
    filterDef.description = catalogFilter.helperText;
  }

  if (catalogFilter.controlType === controlTypes.NUMBER) {
    configureNumberFilter(filterDef, catalogFilter);
  }

  if (catalogFilter.source === lookSources.STATIC && catalogFilter.staticSource) {
    filterDef.options = catalogFilter.staticSource;
  } else if (catalogFilter.source === lookSources.DYNAMIC && catalogFilter.dynamicSource) {
    configureDynamicSource(filterDef, catalogFilter, filterType, zonePrefix, product, catalogId);
  }

  return filterDef;
};

/**
 * Fetches and transforms widget filter configuration from catalog API
 * Falls back to mock data for testing if catalog item not found
 * @param catalogId - The widget catalog ID
 * @returns Promise<FilterDefinition[]> - Transformed filter definitions
 */
export const getWidgetFilterConfig = async (catalogId?: string): Promise<FilterDefinition[]> => {
  if (!catalogId) {
    return [];
  }

  try {
    const { fetchWidgetCatalogDataById } = useDashboardStore();
    const catalogItem = await fetchWidgetCatalogDataById(catalogId);
    if (!catalogItem) {
      console.warn(`No catalog item found for id: ${catalogId}`);
      return [];
    }

    const hasFilters = catalogItem.filters && catalogItem.filters.length > 0;
    const hasDefaultConfig = catalogItem.default_config && catalogItem.default_config.length > 0;

    const zonePrefix = "control-tower";
    const allFilters: FilterDefinition[] = [];

    // Process regular filters (FILTERS section)
    if (hasFilters) {
      const filterPromises = catalogItem.filters.map((filter: any) =>
        transformCatalogFilterToDefinition(filter as WidgetFilterConfig, zonePrefix, catalogItem.product, catalogId)
      );

      const filters = await Promise.all(filterPromises);

      // Add to FILTERS section
      const filtersWithSection = filters.map((filter) => ({
        ...filter,
        groupId: "widget-filters",
        sectionLabel: "CONTROL_TOWER.WIDGET_FILTERS.FILTERS",
        sectionIcon: "sliders-horizontal"
      }));

      allFilters.push(...filtersWithSection);
    }
    if (hasDefaultConfig) {
      const thresholds = catalogItem.default_config.flatMap(
        (config: any, index: number) => transformDefaultConfigToDefinition(config as DefaultConfigItem, index) // threshold
      );

      // Some widgets need the threshold spelled out — the number alone doesn't say
      // what it is measured against (see THRESHOLD_LABEL_OVERRIDES).
      const thresholdLabel = THRESHOLD_LABEL_OVERRIDES[catalogItem.widget_catalog_type ?? ""];

      // Add to THRESHOLDS section
      const thresholdsWithSection = thresholds.map((threshold) => ({
        ...threshold,
        ...(thresholdLabel ? { label: thresholdLabel, hiddenlabel: false } : {}),
        groupId: "widget-thresholds",
        sectionLabel: "CONTROL_TOWER.WIDGET_FILTERS.THRESHOLDS",
        sectionIcon: "calendar-clock"
      }));

      allFilters.push(...thresholdsWithSection);
    }

    return allFilters;
  } catch (error) {
    logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widget filter config for catalog ${catalogId}: ${error}`);
    return [];
  }
};
